import { Button, Icon, Tooltip } from '@cotera/client/app/components';
import { FormLabel } from '@cotera/client/v0/components/ui/form';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@cotera/client/v0/components/ui/select';
import { Assert } from '@cotera/utilities';
import { useState } from 'react';

export const ItemHeader: React.FC<{
  displayName: string;
  toolTipText: string | null;
  optional: boolean;
  description: string | null;
}> = ({ displayName, toolTipText, optional, description }) => {
  return (
    <div className="flex flex-row items-center space-x-2 flex-nowrap pb-2">
      {toolTipText !== null ? (
        <Tooltip side="top" tooltipContent={toolTipText}>
          <FormLabel required={!optional}>{displayName}</FormLabel>
        </Tooltip>
      ) : (
        <FormLabel required={!optional}>{displayName}</FormLabel>
      )}
      {description && <InfoIcon description={description} />}
      {optional && (
        <>
          <span className="text-muted-foreground">-</span>
          <span className="text-muted-foreground text-sm">Optional</span>
        </>
      )}
    </div>
  );
};

const InfoIcon: React.FC<{
  description: string;
}> = ({ description }) => {
  return (
    <div className="flex items-center gap-1">
      <Tooltip tooltipContent={description} side="top" trigger="hover">
        <div className="cursor-pointer flex items-center shrink-0">
          <Icon
            icon="information-circle"
            className="text-muted-foreground"
            size="large"
          />
        </div>
      </Tooltip>
    </div>
  );
};

/**
 * Renders a Shadcn Select field with initialValue selected.
 *
 * Invokes onChange handler whenever the user changes selection.
 *
 * `options` should not include the default value or placeholder.
 *
 * The default value (if user does not select anything, or clears a current selection) is empty string
 * (in which case, the placeholder is rendered).
 */
export const ShadcnSelect: React.FC<{
  options: (readonly [value: string, label: string])[];
  initialValue: string;
  placeHolder: string;
  required: boolean;
  onChange: (newValue: string) => void;
}> = ({ options, initialValue, placeHolder, required, onChange }) => {
  Assert.assert(
    initialValue === '' ||
      options.map(([value, _]) => value).includes(initialValue)
  );

  const [state, setState] = useState(initialValue);

  return (
    <>
      <Select
        value={state}
        required={required}
        onValueChange={(newValue) => {
          setState(newValue);
          onChange(newValue);
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeHolder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(([value, label]) => (
            <SelectItem value={value}>{label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!required && (
        <Button
          icon="x-mark"
          onClick={() => {
            setState('');
            onChange('');
          }}
        />
      )}
    </>
  );
};
